import { createContext, useCallback, useEffect, useRef, type ReactNode } from 'react'
import { useAuth } from '@workos-inc/authkit-react'
import { useApi } from '@/shared/utils/request'
import { logger } from '@/shared/utils/logger'
import { getAdminPermissions } from '@/shared/config/adminPermissions'
import { createCacheStore, type CacheStore } from './store'
import { cacheSlices, type CacheSliceKey, type CacheSliceData } from './slices'
import type { AuthSliceData } from './slices/auth'
import type { ApiClient, CacheSliceConfig, CacheSliceState } from './types'

type SliceConfig = CacheSliceConfig<any>

type CacheContextValue = {
  store: CacheStore
  refresh: (key: CacheSliceKey) => Promise<void>
  refreshAll: () => Promise<void>
  optimisticUpdate: <K extends CacheSliceKey>(
    key: K,
    updater: (prev: CacheSliceData<K>) => CacheSliceData<K>,
  ) => () => void
}

export const CacheContext = createContext<CacheContextValue | null>(null)

export function CacheProvider({ children }: { children: ReactNode }) {
  const { user, isLoading: isAuthLoading } = useAuth()
  const { makeApiRequest } = useApi()
  const apiRef = useRef<ApiClient>({ makeApiRequest })
  apiRef.current = { makeApiRequest }

  const storeRef = useRef<CacheStore>(null!)
  if (storeRef.current === null) {
    storeRef.current = createCacheStore(cacheSlices)
  }
  const store = storeRef.current

  // Scope localStorage keys to the current user to prevent cross-user data leakage
  const userIdRef = useRef<string | undefined>(undefined)
  userIdRef.current = user?.id

  const scopedPersistKey = useCallback(
    (baseKey: string) => {
      const uid = userIdRef.current
      return uid ? `${baseKey}:${uid}` : baseKey
    },
    [],
  )

  const fetchSlice = useCallback(
    async (key: CacheSliceKey) => {
      const config = cacheSlices[key] as SliceConfig
      const current = store.getSlice(key)

      store.setSlice(key, { ...current, isLoading: true, error: null })

      try {
        const data = await config.fetch(apiRef.current)

        const newState: CacheSliceState<any> = {
          data,
          isLoading: false,
          error: null,
          lastFetchedAt: Date.now(),
        }
        store.setSlice(key, newState)

        // Persist to localStorage if configured (scoped to user)
        if (config.persistKey) {
          try {
            localStorage.setItem(
              scopedPersistKey(config.persistKey),
              JSON.stringify({ data, fetchedAt: newState.lastFetchedAt }),
            )
          } catch {
            // localStorage full or unavailable — non-critical
          }
        }

        logger.debug(`Cache slice "${key}" fetched`)
      } catch (e) {
        const current = store.getSlice(key)
        store.setSlice(key, {
          ...current,
          isLoading: false,
          error: e instanceof Error ? e.message : 'Fetch failed',
        })
        if (e instanceof Error) {
          logger.captureException(e, { action: `cache:fetch:${key}` })
        }
      }
    },
    [store, scopedPersistKey],
  )

  const refresh = useCallback(
    async (key: CacheSliceKey) => {
      await fetchSlice(key)
    },
    [fetchSlice],
  )

  const refreshAll = useCallback(async () => {
    const keys = (Object.keys(cacheSlices) as CacheSliceKey[]).filter((k) => k !== 'auth')
    await Promise.all(keys.map((key) => fetchSlice(key)))
  }, [fetchSlice])

  const optimisticUpdate = useCallback(
    <K extends CacheSliceKey>(
      key: K,
      updater: (prev: CacheSliceData<K>) => CacheSliceData<K>,
    ): (() => void) => {
      const current = store.getSlice<CacheSliceData<K>>(key)
      const snapshot = { ...current }

      store.setSlice(key, {
        ...current,
        data: updater(current.data),
      })

      // Return rollback function
      return () => {
        store.setSlice(key, snapshot)
      }
    },
    [store],
  )

  // Hydrate from localStorage + initial fetch on auth ready
  useEffect(() => {
    if (isAuthLoading || !user) return

    // Populate auth slice (computed from WorkOS user, not fetched)
    const { isAdmin: userIsAdmin, canView } = getAdminPermissions(user.email)
    const authData: AuthSliceData = {
      userId: user.id,
      email: user.email,
      firstName: user.firstName ?? null,
      lastName: user.lastName ?? null,
      displayName: user.firstName && user.lastName
        ? `${user.firstName} ${user.lastName}`
        : user.email,
      initials: user.firstName && user.lastName
        ? `${user.firstName[0]}${user.lastName[0]}`.toUpperCase()
        : user.email?.[0]?.toUpperCase() ?? '?',
      isAdmin: userIsAdmin,
      adminPermissions: canView,
    }
    store.setSlice('auth', {
      data: authData,
      isLoading: false,
      error: null,
      lastFetchedAt: Date.now(),
    })

    // Fetch all API-backed slices (exclude auth — it's computed above)
    const allKeys = (Object.keys(cacheSlices) as CacheSliceKey[]).filter((k) => k !== 'auth')

    const keys = allKeys

    // Hydrate from localStorage first (stale-while-revalidate, scoped to user)
    for (const key of keys) {
      const config = cacheSlices[key] as SliceConfig
      if (!config.persistKey) continue

      try {
        const cached = localStorage.getItem(scopedPersistKey(config.persistKey))
        if (cached) {
          const { data, fetchedAt } = JSON.parse(cached)
          store.setSlice(key, {
            data,
            isLoading: true, // still loading — fresh fetch incoming
            error: null,
            lastFetchedAt: fetchedAt,
          })
        }
      } catch {
        // Corrupted localStorage — ignore, fresh fetch will fix it
      }
    }

    // Fetch only the slices needed for the current route
    keys.forEach((key) => fetchSlice(key))
  }, [isAuthLoading, user, store, fetchSlice, scopedPersistKey])

  // Reset in-memory store on logout so the next user doesn't see stale data
  const prevUserRef = useRef<string | undefined>(undefined)
  useEffect(() => {
    const prevUserId = prevUserRef.current
    prevUserRef.current = user?.id
    if (prevUserId && !user) {
      // User just logged out — reset store to initial values
      const keys = Object.keys(cacheSlices) as CacheSliceKey[]
      for (const key of keys) {
        const config = cacheSlices[key] as SliceConfig
        store.setSlice(key, {
          data: config.initialValue,
          isLoading: false,
          error: null,
          lastFetchedAt: 0,
        })
      }
    }
  }, [user, store])

  // Window focus listener for "on-focus" slices
  useEffect(() => {
    if (!user) return

    const handleFocus = () => {
      const allKeys = Object.keys(cacheSlices) as CacheSliceKey[]
      for (const key of allKeys) {
        if (cacheSlices[key].refetch !== 'on-focus') continue
        fetchSlice(key)
      }
    }

    window.addEventListener('focus', handleFocus)
    return () => window.removeEventListener('focus', handleFocus)
  }, [user, fetchSlice])

  const contextValue: CacheContextValue = {
    store,
    refresh,
    refreshAll,
    optimisticUpdate,
  }

  return <CacheContext.Provider value={contextValue}>{children}</CacheContext.Provider>
}
